import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins, Diamond, TrendingUp, TrendingDown, Filter, Calendar, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { format, startOfDay, startOfWeek, isToday, isThisWeek } from 'date-fns';

interface Transaction {
  id: string;
  transaction_type: 'earned' | 'spent';
  currency_type: 'coins' | 'gems';
  amount: number;
  reason: string;
  source_data: any;
  created_at: string;
}

const reasonLabels: Record<string, string> = {
  bot_game_completion: '🤖 Bot Game',
  multiplayer_win: '🏆 Multiplayer Win',
  puzzle_completion: '🧩 Puzzle Solved',
  daily_bonus: '📅 Daily Bonus',
  game_completion: '♟️ Game Completed',
  tutorial_completion: '📖 Tutorial Done',
  shop_purchase: '🛒 Shop Purchase',
};

export default function CurrencyHistory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency } = useCurrency();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'earned' | 'spent'>('all');
  const [reasonFilter, setReasonFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  const pageSize = 50;

  const fetchTransactions = async (reset = false) => {
    if (!user) return;
    
    const currentPage = reset ? 0 : page;
    if (reset) setPage(0);
    
    setLoading(true);
    
    let query = supabase
      .from('currency_transactions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .range(currentPage * pageSize, (currentPage + 1) * pageSize - 1);
    
    if (filter !== 'all') {
      query = query.eq('transaction_type', filter);
    }
    
    if (reasonFilter !== 'all') {
      query = query.eq('reason', reasonFilter);
    }
    
    const { data, error } = await query;
    
    if (!error && data) {
      if (reset) {
        setTransactions(data as Transaction[]);
      } else {
        setTransactions(prev => [...prev, ...(data as Transaction[])]);
      }
      setHasMore(data.length === pageSize);
    }
    
    setLoading(false);
  };

  useEffect(() => {
    fetchTransactions(true);
  }, [user, filter, reasonFilter]);

  const loadMore = () => {
    setPage(prev => prev + 1);
    fetchTransactions();
  };

  // Calculate stats
  const todayEarned = transactions
    .filter(t => t.transaction_type === 'earned' && t.currency_type === 'coins' && isToday(new Date(t.created_at)))
    .reduce((sum, t) => sum + t.amount, 0);

  const weekEarned = transactions
    .filter(t => t.transaction_type === 'earned' && t.currency_type === 'coins' && isThisWeek(new Date(t.created_at)))
    .reduce((sum, t) => sum + t.amount, 0);

  const uniqueReasons = [...new Set(transactions.map(t => t.reason))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-yellow-500 to-amber-500 bg-clip-text text-transparent">
              Currency History
            </h1>
            <p className="text-muted-foreground text-sm">Track your coins and gems</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border-yellow-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-yellow-500">
                <Coins className="h-5 w-5" />
                <span className="text-sm font-medium">Balance</span>
              </div>
              <p className="text-2xl font-bold mt-1">{currency?.coins?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-500">
                <Diamond className="h-5 w-5" />
                <span className="text-sm font-medium">Gems</span>
              </div>
              <p className="text-2xl font-bold mt-1">{currency?.gems?.toLocaleString() || 0}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-500">
                <TrendingUp className="h-5 w-5" />
                <span className="text-sm font-medium">Today</span>
              </div>
              <p className="text-2xl font-bold mt-1">+{todayEarned.toLocaleString()}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border-purple-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-purple-500">
                <Calendar className="h-5 w-5" />
                <span className="text-sm font-medium">This Week</span>
              </div>
              <p className="text-2xl font-bold mt-1">+{weekEarned.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Transactions
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => fetchTransactions(true)}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filter Tabs */}
            <Tabs value={filter} onValueChange={(v) => setFilter(v as any)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="earned" className="text-green-500">Earned</TabsTrigger>
                <TabsTrigger value="spent" className="text-red-500">Spent</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Reason Filter */}
            <Select value={reasonFilter} onValueChange={setReasonFilter}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Reasons</SelectItem>
                {uniqueReasons.map(reason => (
                  <SelectItem key={reason} value={reason}>
                    {reasonLabels[reason] || reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Transaction List */}
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {loading && transactions.length === 0 ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))
                ) : transactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No transactions found
                  </div>
                ) : (
                  transactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          tx.transaction_type === 'earned' 
                            ? 'bg-green-500/20 text-green-500' 
                            : 'bg-red-500/20 text-red-500'
                        }`}>
                          {tx.transaction_type === 'earned' ? (
                            <TrendingUp className="h-4 w-4" />
                          ) : (
                            <TrendingDown className="h-4 w-4" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {reasonLabels[tx.reason] || tx.reason}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(tx.created_at), 'MMM d, yyyy • h:mm a')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${
                          tx.transaction_type === 'earned' ? 'text-green-500' : 'text-red-500'
                        }`}>
                          {tx.transaction_type === 'earned' ? '+' : '-'}{tx.amount.toLocaleString()}
                        </span>
                        {tx.currency_type === 'coins' ? (
                          <Coins className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <Diamond className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                    </div>
                  ))
                )}
                
                {hasMore && transactions.length > 0 && (
                  <Button
                    variant="outline"
                    className="w-full mt-4"
                    onClick={loadMore}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </Button>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
